import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Alert, Button, Icons, Popup } from '@shopline/dashboard-ui';
import ToastContext from 'contexts/Toast';
import { nanoid } from 'nanoid';
import { useQueryMerchant } from '../useMerchant';
import { PopupContent } from '../components/PopupContent';
import { useQueryInitialState } from 'hooks/useInitialState';
import { Actions, useConfigurationForm } from 'contexts/ConfigurationForm';
import usePermissions from 'hooks/usePermissions';
import CircleTickIcon from '../components/CircleTickIcon';
import {
  useMigrateFromContentApi,
  MigrateFromContentApiResponse,
} from '../useMigrateFromContentApi';
import { useQueryRegionAndLocalesSets } from '../useRegionAndLocaleSets';
import {
  useCancelMigrationFromContentApi,
  CancelMigrationFromContentApiResponse,
} from '../useCancelMigrationFromContentApi';
import { PRODUCT_API_TYPE } from '../constants';
import {
  IntegrationFormSection as BaseIntegrationFormSection,
  IntegrationFormContainer,
  IntegrationFormTitle,
  IntegrationFormDescription,
  GoogleMerchantIdContainer,
  EnhancedWell,
  WellTitle,
  DisintegrateButton,
  HiddenDisintegrateContainer,
  DisconnectHint,
  EnhancedSuccessAlert,
} from './StyledComponents';

const IntegrationFormSection = styled(BaseIntegrationFormSection)`
  margin-bottom: 16px;
`;

const OauthIntegrationForm = () => {
  const { t } = useTranslation(['main', 'common']);
  const { createUIToast } = useContext(ToastContext);
  const [showUnlinkPopup, setShowUnlinkPopup] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const {
    isSuccess: isInitialStateLoaded,
    data: initialState,
    refetch: refetchInitialState,
  } = useQueryInitialState();
  const {
    isSuccess: isMerchantLoaded,
    data: merchant,
    refetch: refetchMerchant,
  } = useQueryMerchant();
  const { refetch: refetchRegionAndLocaleSets } =
    useQueryRegionAndLocalesSets();
  const { dispatch } = useConfigurationForm();
  const { mutate: migrateFromContentApi, isLoading: migratingFromContentApi } =
    useMigrateFromContentApi({
      onSuccess: async (_responseData: MigrateFromContentApiResponse) => {
        const [{ data: merchant }, { data: regionAndLocaleSets }] =
          await Promise.all([refetchMerchant(), refetchRegionAndLocaleSets()]);
        createUIToast({
          id: nanoid(),
          type: 'success',
          titleWithParams: {
            key: 'main:Successfully upgraded to Merchant API',
          },
          duration: 5000,
        });
        dispatch(Actions.setCurrentState(merchant, regionAndLocaleSets));
        setShowSuccessAlert(true);
      },
      onError: (error) => {
        if (error.response?.data?.code === 'Unauthenticated') {
          createUIToast({
            id: nanoid(),
            type: 'warning',
            titleWithParams: {
              key: 'main:Your current Google account does not have administrative access to this GMC account. Please contact the store owner for authorization.',
            },
            duration: 5000,
          });
          return;
        }
        if (error.response?.data?.code === 'InsufficientAuthenticationScope') {
          createUIToast({
            id: nanoid(),
            type: 'warning',
            titleWithParams: {
              key: 'main:Insufficient permissions. Please log in to Google again and grant the required access.',
            },
            duration: 5000,
          });
          return;
        }
        createUIToast({
          id: nanoid(),
          type: 'alert',
          titleWithParams: {
            key: 'common:Something went wrong. Please try again.',
          },
          duration: 5000,
        });
      },
    });
  const {
    mutate: cancelMigrationFromContentApi,
    isLoading: cancelingMigrationFromContentApi,
  } = useCancelMigrationFromContentApi({
    onSuccess: (_responseData: CancelMigrationFromContentApiResponse) => {
      setShowUnlinkPopup(false);
      refetchMerchant();
      refetchInitialState();
    },
    onError: (error) => {
      console.error(error);
      setShowUnlinkPopup(false);
      createUIToast({
        id: nanoid(),
        type: 'alert',
        titleWithParams: {
          key: 'common:Something went wrong. Please try again.',
        },
        duration: 5000,
      });
    },
  });
  const canEdit = usePermissions('merchant', 'edit');

  const merchantId = initialState?.merchant?.id;
  const mcaIntegrated =
    merchant?.productApiType === PRODUCT_API_TYPE.MERCHANT_API;

  if (!isInitialStateLoaded || !isMerchantLoaded) {
    return null;
  }
  return (
    <>
      {showSuccessAlert && (
        <EnhancedSuccessAlert
          showCloseButton={true}
          onClose={() => setShowSuccessAlert(false)}
        >
          {t(
            'Content API has been upgraded to Merchant API. Your previous settings have been automatically applied. No reconfiguration is needed.',
          )}
        </EnhancedSuccessAlert>
      )}
      <IntegrationFormSection
        title={mcaIntegrated ? t('Merchant Center ID') : null}
      >
        {!mcaIntegrated && (
          <>
            <IntegrationFormContainer>
              <div>
                <IntegrationFormTitle>
                  {t('Upgrade Content API to Merchant API')}
                </IntegrationFormTitle>
                <IntegrationFormDescription>
                  <ul>
                    <li>
                      {t(
                        'Due to Google policy changes, the Content API will be deprecated on 18 August 2026, and replaced by the Merchant API.',
                      )}
                    </li>
                    <li>
                      {t(
                        'Please complete the API upgrade as soon as possible to prevent any disruption to product uploads and ad delivery.',
                      )}
                    </li>
                    <li>
                      {t(
                        'Once authorized, you can upgrade to the Merchant API instantly with no manual setup required.',
                      )}
                    </li>
                  </ul>
                </IntegrationFormDescription>
              </div>
              <div>
                <Button.Primary
                  type="button"
                  onClick={() => migrateFromContentApi(merchantId)}
                  isLoading={migratingFromContentApi}
                  disabled={!canEdit}
                >
                  {t('Upgrade now', { ns: 'common' })}
                </Button.Primary>
              </div>
            </IntegrationFormContainer>
            <Alert.Warning>
              {t(
                "We recommend that the store owner perform the authorization to avoid disruptions caused by changes to a staff member's account status or permissions.",
              )}
            </Alert.Warning>
          </>
        )}
        {mcaIntegrated && (
          <>
            <GoogleMerchantIdContainer>
              <WellTitle>{t('Connected Merchant Center ID')}</WellTitle>
              <EnhancedWell>
                <div>
                  <CircleTickIcon size="MEDIUM" />
                  {merchant?.googleMerchantId}
                </div>
                {/* Hidden because the MCA parent account is currently inactive, so merchants cannot
                re-integrate via the MCA flow after disconnecting. Show this again once MCA is ready. */}
                <HiddenDisintegrateContainer>
                  <DisintegrateButton
                    onClick={() => setShowUnlinkPopup(true)}
                    disabled={!canEdit}
                  >
                    <Icons.Outline.Unlink size="MEDIUM" />
                    {t('Disconnect')}
                  </DisintegrateButton>
                </HiddenDisintegrateContainer>
              </EnhancedWell>
              <DisconnectHint>
                {t(
                  'If you need to disconnect Google Merchant Center, please contact the Online Merchant Success Team for assistance.',
                )}
              </DisconnectHint>
            </GoogleMerchantIdContainer>
            {showUnlinkPopup && (
              <Popup
                width="480px"
                title={t('Disconnect Google Merchant Center?')}
                showCloseBtn={true}
                onClose={() => setShowUnlinkPopup(false)}
                renderFooter={() => (
                  <>
                    <Button.Default
                      height="LARGE"
                      onClick={() => setShowUnlinkPopup(false)}
                    >
                      {t('Cancel', { ns: 'common' })}
                    </Button.Default>
                    <Button.Destructive
                      height="LARGE"
                      onClick={() => cancelMigrationFromContentApi(merchantId)}
                      isLoading={cancelingMigrationFromContentApi}
                    >
                      {t('Confirm Disconnect')}
                    </Button.Destructive>
                  </>
                )}
              >
                <PopupContent>
                  <ol>
                    <li>
                      {t(
                        'Product data syncing will stop, and ads linked to this account will be interrupted.',
                      )}
                    </li>
                    <li>
                      {t(
                        'To reconnect to Google Merchant Center, a new GMC account will be created following the new process to comply with Google\'s latest API standards. Please contact the Online Merchant Success Team to enable the "Automatically create and connect Merchant API" feature.',
                      )}
                    </li>
                  </ol>
                </PopupContent>
              </Popup>
            )}
          </>
        )}
      </IntegrationFormSection>
    </>
  );
};

export default OauthIntegrationForm;
